import { useCallback, useMemo, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Let2OffsetRenewalUiMode } from '@havenengineering/module-shared-owners-library/dist/enums/let2offsetRenewalUiMode';
import {
  Let2offsetWarningModal,
  OptOutReason,
} from '@havenengineering/module-shared-owners-ui';
import { Let2offsetApplicationCard } from '@havenengineering/module-shared-owners-ui/dist/components/Let2offsetApplicationCard/Let2offsetApplicationCard';

import { fetchWrapper, withApiBaseUrl } from '../../helpers/fetch';
import { parseLet2offsetRenewalUiMode } from '../../helpers/lettingMarketing';
import { LettingConfigEntry } from '../../types/lettingConfig';
import { LettingAccount } from '../../types/lettings';

export interface AccountUpdate {
  accountNumber: string;
  isRenewing: boolean;
  optOutReason?: OptOutReason;
}

interface Let2offsetApplicationProps {
  activeAccount: UserAccount;
  lettingAccount: LettingAccount;
  lettingConfig: LettingConfigEntry[];
}

export const Let2offsetApplication = ({
  activeAccount,
  lettingAccount,
  lettingConfig,
}: Let2offsetApplicationProps) => {
  const [openLet2offsetWarningModal, setOpenLet2offsetWarningModal] =
    useState<boolean>(false);

  const renewalUiMode = useMemo(
    () => parseLet2offsetRenewalUiMode(lettingConfig),
    [lettingConfig]
  );

  const showLet2OwnBanner = useMemo(
    () =>
      renewalUiMode &&
      renewalUiMode !== Let2OffsetRenewalUiMode.UNSET &&
      (lettingAccount?.isRenewing || lettingAccount?.readyForRenewal),
    [renewalUiMode, lettingAccount?.isRenewing, lettingAccount?.readyForRenewal]
  );

  const handleLetToOwnRenewal = useCallback(
    async (accountUpdate: AccountUpdate, redirectUrl: string) => {
      try {
        await fetchWrapper(withApiBaseUrl(`/letting/account`), {
          method: 'PUT',
          credentials: 'include',
          body: JSON.stringify(accountUpdate),
        });
      } catch (error) {
        datadogRum.addError(error, {
          context: 'let2offsetApplication.renewalOptInOut',
          isRenewing: accountUpdate.isRenewing,
          optOutReason: accountUpdate.optOutReason,
        });
        throw error;
      }

      window.location.assign(redirectUrl);
    },
    []
  );

  const handleOptIn = useCallback(
    () =>
      handleLetToOwnRenewal(
        {
          accountNumber: activeAccount?.accountNo as string,
          isRenewing: true,
        },
        '/renewals'
      ).catch(() => {
        // Already reported to RUM; the opt-in buttons cannot show an error.
      }),
    [activeAccount?.accountNo, handleLetToOwnRenewal]
  );

  const handleOptOut = useCallback(
    (optOutReason: OptOutReason) =>
      handleLetToOwnRenewal(
        {
          accountNumber: activeAccount?.accountNo as string,
          isRenewing: false,
          optOutReason,
        },
        '/letting'
      ),
    [activeAccount?.accountNo, handleLetToOwnRenewal]
  );

  if (!showLet2OwnBanner) return null;

  return (
    <>
      <Let2offsetWarningModal
        isOpen={openLet2offsetWarningModal}
        close={() => setOpenLet2offsetWarningModal(false)}
        handleOptIn={handleOptIn}
        handleOptOut={handleOptOut}
      />
      <Let2offsetApplicationCard
        handleContinueWithRenewal={async () =>
          window.location.assign('/let2offset-renewal')
        }
        handleLetToOwnRenewal={handleOptIn}
        isRenewing={lettingAccount.isRenewing}
        renewalWindowOpen={renewalUiMode === Let2OffsetRenewalUiMode.RENEWAL}
        setOpenLet2OwnWarningModal={setOpenLet2offsetWarningModal}
        videoUrl="https://videos.ctfassets.net/54gbssdap0o8/5YpRAYeETIsvTju0MPt7sD/d6ca9d93f0b4c9f77235b285f7c900b1/l2o-renewal-video-compressed.mp4"
      />
    </>
  );
};
